// Payment domain types - 1:1 with Schema_Specification Section 3.6 (2.7.x).

import i18n from '@/i18n'
import type { ListQuery } from './index'
import type { Client } from './clients'
import type { Invoice } from './billing'

export type PaymentMethod = 'cash' | 'qr' | 'cheque' | 'transfer' | 'card'

export type CreatePaymentMethod = Exclude<PaymentMethod, 'qr'>

// Payment status (feedback #26). A payment is Pending, then manually moved to
// Completed (paid) or Failed; Refunded handles refunds. There is no `partial`
// status - partial settlement is derived on the invoice (partially_paid).
export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded'

// Captured reason a payment landed in `failed` (feedback #26).
export type PaymentFailureReason =
  | 'not_received_by_due_date'
  | 'partial_payment'
  | 'invoice_disputed'
  | 'system_behavior'
  | 'stripe_payment_failed'

export interface Payment {
  id: string
  tenant_id: string
  reference: string | null
  client_id: string
  invoice_id: string | null
  job_id: string | null
  amount: number
  method: PaymentMethod
  status: PaymentStatus
  // Reason a payment failed (feedback #26); null unless status === 'failed'.
  failure_reason?: PaymentFailureReason | null
  stripe_payment_intent_id: string | null
  proof_file_id: string | null
  // Short-lived presigned S3 URL for the proof file (detail response only).
  proof_url?: string | null
  proof_files?: PaymentProofFile[]
  paid_date: string | null
  failed_at?: string | null
  refunded_at?: string | null
  note: string | null
  collected_by: string | null
  reminder_count?: number
  created_at: string
  updated_at: string
  // Derived/list-only display fields surfaced by the backend.
  client?: Client | null
  client_name?: string | null
  invoice_number?: string | null
  job_reference?: string | null
  proof_file_name?: string | null
  proof_file_size?: number | null
  // Stripe-hosted Checkout URL. Returned ONLY in the create response for online
  // card payments; not persisted, absent from GET /payments/{id}.
  checkout_url?: string | null
}

export interface PaymentDetail extends Payment {
  client: Client
  invoice: Invoice | null
}

export interface PaymentProofFile {
  id: string
  name: string
  size: number | null
  mime_type: string | null
  url: string | null
  created_at?: string | null
}

export interface CreatePaymentRequest {
  client_id: string
  invoice_id?: string
  job_id?: string
  amount: number
  method: CreatePaymentMethod
  status?: PaymentStatus
  paid_date?: string
  reference_number?: string
  note?: string
}

export interface CashPaymentRequest {
  client_id: string
  invoice_id?: string
  job_id?: string
  amount: number
  note?: string
}

export interface UpdatePaymentStatusRequest {
  status: PaymentStatus
  // Required only when status === 'failed' (feedback #26).
  failure_reason?: PaymentFailureReason
  note?: string
}

export interface UploadProofRequest {
  files: Array<File | Blob>
}

export interface PaymentListQuery extends ListQuery {
  status?: string | string[]
  method?: string
  client_id?: string
  invoice_id?: string
  invoice_status?: string | string[]
  job_id?: string
  date_from?: string
  date_to?: string
}

export interface PaymentSummary {
  total_received: number
  total_received_count: number
  this_month: number
  this_month_count: number
  awaiting_collection: number
  awaiting_collection_count: number
  remaining_amount_due: number
  remaining_amount_due_count: number
}

// --- Display label maps (Test_Cases / AC labels for 2.7.x) ---
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: i18n.t('enums:paymentMethod.cash'),
  qr: i18n.t('enums:paymentMethod.qr'),
  cheque: i18n.t('enums:paymentMethod.cheque'),
  transfer: i18n.t('enums:paymentMethod.transfer'),
  card: i18n.t('enums:paymentMethod.card'),
}

// Status labels per AC-2.7.1-026 (Pending / Completed / Failed), plus Refunded
// for refund handling (2.7.x). "Partial" was removed (feedback #26).
export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: i18n.t('enums:paymentStatus.pending'),
  paid: i18n.t('enums:paymentStatus.paid'),
  failed: i18n.t('enums:paymentStatus.failed'),
  refunded: i18n.t('enums:paymentStatus.refunded'),
}

// Failure sub-reason labels (feedback #26), shown when a payment is marked Failed.
export const PAYMENT_FAILURE_REASON_LABELS: Record<
  PaymentFailureReason,
  string
> = {
  not_received_by_due_date: i18n.t(
    'enums:paymentFailureReason.not_received_by_due_date'
  ),
  partial_payment: i18n.t('enums:paymentFailureReason.partial_payment'),
  invoice_disputed: i18n.t('enums:paymentFailureReason.invoice_disputed'),
  system_behavior: i18n.t('enums:paymentFailureReason.system_behavior'),
  stripe_payment_failed: i18n.t(
    'enums:paymentFailureReason.stripe_payment_failed'
  ),
}
